/**
 * Range Slider it allows user to move the slider to select min and max value in passed range
 */
import React, { useRef } from 'react'
import PropTypes from 'prop-types'

import { useSliderPosition } from './hooks/useSliderPosition'
import { formatNumberWithUnit, numberFormatter } from '../../Helpers'
import './index.css'

// eslint-disable-next-line max-lines-per-function
export const RangeSlider = (props) => {
  const { min, max, unboundedMax, className, defaultMin, defaultMax, stepSize = 1, prefix = '', onChange } = props

  const rangeMax = (max - min) % stepSize ? max + stepSize - ((max - min) % stepSize) : max
  const minSliderRef = useRef(null)
  const maxSliderRef = useRef(null)
  const sliderTrackRef = useRef(null)
  const { state, setStartValue, setEndValue } = useSliderPosition(
    min,
    rangeMax,
    stepSize,
    defaultMin,
    defaultMax,
    minSliderRef.current,
    maxSliderRef.current,
    sliderTrackRef.current
  )

  const { start, end } = state
  const postfix = unboundedMax && end >= max ? '+' : ''
  const listArray = [min, Math.round((max + min) / 2), max]

  const selectedMin = start > max ? max : start
  const selectedMax = end > max ? max : end
  const rangeText = `${prefix + numberFormatter(selectedMin)} - ${prefix + numberFormatter(selectedMax) + postfix}`

  const handleMouseUp = () => {
    onChange({
      minValue: selectedMin,
      maxValue: selectedMax,
    })
  }

  return (
    <div className="range-slider__container">
      <div aria-valuetext={rangeText} className="range-slider__text">
        {rangeText}
      </div>
      <div className="range-slider">
        <ul>
          {listArray.map((amount, index) => (
            <li key={amount} aria-hidden="true">
              <span className={`range-slider__span ${index === 2 ? 'range-slider__span--right' : ''}`}>
                {prefix}
                {formatNumberWithUnit(amount)}
              </span>
            </li>
          ))}
        </ul>
        <div aria-hidden="true" className="range-slider__div" ref={sliderTrackRef} />
        <input
          ref={minSliderRef}
          min={min}
          max={rangeMax}
          type="range"
          step={stepSize}
          className={className}
          aria-valuemin={min}
          aria-valuenow={start}
          aria-describedby="sliderDescriptionMinRange"
          aria-valuetext={prefix + start}
          aria-valuemax={end}
          aria-label={prefix + start}
          onMouseUp={() => handleMouseUp()}
          onTouchStart={(event) => {
            event.stopPropagation()
            minSliderRef.current?.focus()
            maxSliderRef.current?.blur()
          }}
          onTouchEnd={() => handleMouseUp()}
          onChange={(event) => {
            event.stopPropagation()
            setStartValue(event.target.valueAsNumber > end ? end : event.target.valueAsNumber)
          }}
          value={start}
        />
        <input
          ref={maxSliderRef}
          min={min}
          max={rangeMax}
          type="range"
          step={stepSize}
          className={className}
          aria-valuemin={start}
          aria-valuenow={end}
          aria-describedby="sliderDescriptionMaxRange"
          aria-valuetext={prefix + end}
          aria-valuemax={max}
          aria-label={prefix + end}
          onMouseUp={() => handleMouseUp()}
          onTouchStart={(event) => {
            event.stopPropagation()
            minSliderRef.current?.blur()
            maxSliderRef.current?.focus()
          }}
          onTouchEnd={() => handleMouseUp()}
          onChange={(event) => {
            event.stopPropagation()
            setEndValue(event.target.valueAsNumber < start ? start : event.target.valueAsNumber)
          }}
          value={end}
        />
      </div>
    </div>
  )
}

export default RangeSlider

RangeSlider.defaultProps = {
  defaultMin: null,
  defaultMax: null,
  className: '',
}

RangeSlider.propTypes = {
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  unboundedMax: PropTypes.bool.isRequired,
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  defaultMin: PropTypes.number,
  defaultMax: PropTypes.number,
  stepSize: PropTypes.number,
  prefix: PropTypes.string,
}
